<template>
  <div class="auth-card modal-content p-4">
    <!-- ═══ SIGN IN: single credentials step ═══ -->
    <AuthLoginForm
      v-if="authStore.mode === 'signin'"
      :mobile="authStore.loginMobile"
      :password="authStore.loginPassword"
      :status="authStore.status"
      :error-message="currentErrorMessage"
      :signup-success-message="authStore.signupSuccess"
      @submit="handleLogin"
      @update:mobile="updateLoginMobile"
      @update:password="updateLoginPassword"
      @switch-mode="handleModeToggle"
      @forgot-password="handleForgotPassword"
    />

    <!-- ═══ SIGN UP: details step ═══ -->
    <AuthSignupDetailsForm
      v-else-if="authStore.mode === 'signup' && authStore.step === 'details'"
      :signup-draft="authStore.signupDraft"
      :field-errors="signupFieldErrors"
      :status="authStore.status"
      :error-message="signupErrorMessage"
      @update:signup-draft="updateSignupDraft"
      @submit="handleSignupSendOtp"
      @switch-mode="handleModeToggle"
    />

    <!-- ═══ SIGN UP: OTP verification step ═══ -->
    <AuthOtpStep
      v-else-if="authStore.mode === 'signup' && authStore.step === 'signup-otp'"
      mode="signup"
      :phone-mask="signupPhoneMask"
      :otp="authStore.signupOtp.otp"
      :attempts-left="authStore.signupOtp.attemptsLeft"
      :otp-countdown-seconds="authStore.signupOtpCountdownSeconds"
      :resend-countdown-seconds="authStore.signupResendCountdownSeconds"
      :can-resend="authStore.canResendSignupOtp"
      :status="authStore.status"
      :error-message="currentErrorMessage"
      :mock-otp-hint="authStore.signupOtp.mockOtpHint"
      @update:otp="updateSignupOtp"
      @submit="handleSignupVerifyOtp"
      @resend="handleSignupResendOtp"
      @back="handleSignupOtpBack"
    />

    <!-- ═══ FORGOT PASSWORD: OTP-based reset ═══ -->
    <AuthForgotPasswordForm
      v-else-if="authStore.mode === 'forgot-password'"
      :forgot-draft="authStore.forgotDraft"
      :status="authStore.status"
      :error-message="currentErrorMessage"
      :can-resend="authStore.canResendForgotOtp"
      :otp-countdown-seconds="authStore.forgotOtpCountdownSeconds"
      :resend-countdown-seconds="authStore.forgotResendCountdownSeconds"
      @send-otp="handleForgotSendOtp"
      @resend-otp="handleForgotResendOtp"
      @reset-password="handleResetPassword"
      @update:forgot-draft="updateForgotDraft"
      @back="handleBackToSignin"
    />
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, watch } from 'vue'
import AuthLoginForm from '@/components/auth/AuthLoginForm.vue'
import AuthSignupDetailsForm from '@/components/auth/AuthSignupDetailsForm.vue'
import AuthOtpStep from '@/components/auth/AuthOtpStep.vue'
import AuthForgotPasswordForm from '@/components/auth/AuthForgotPasswordForm.vue'
import { useAuthStore } from '@/stores/auth'

const props = defineProps({
  initialMode: {
    type: String,
    default: 'signin'
  }
})

const emit = defineEmits(['success'])

const authStore = useAuthStore()

let clockIntervalId = null

const normalizeMode = (value) => {
  const normalized = String(value ?? '').trim().toLowerCase()
  if (normalized === 'signup') return 'signup'
  if (normalized === 'forgot-password') return 'forgot-password'
  return 'signin'
}

// ─── Error messages ──────────────────────────────────────────────

const currentErrorMessage = computed(() => {
  const code = authStore.error?.code
  if (!code) return ''

  const messages = {
    INVALID_OTP: 'The OTP entered is incorrect. Please try again.',
    OTP_EXPIRED: 'OTP has expired. Please resend and try again.',
    MAX_ATTEMPTS: 'Maximum OTP attempts reached. Resend OTP to continue.',
    ACCOUNT_NOT_FOUND: 'Account not found for this phone number.',
    INVALID_CREDENTIALS: 'Invalid mobile number or password.',
    ACCOUNT_EXISTS: 'Account already exists. Please sign in.',
    NETWORK_ERROR: 'Unable to reach auth service. Please try again.'
  }

  return messages[code] || authStore.error?.message || 'Authentication failed. Please try again.'
})

const signupFieldErrors = computed(() => {
  if (authStore.error?.code === 'VALIDATION_ERROR') {
    return authStore.error?.meta?.fields || {}
  }
  return {}
})

const signupErrorMessage = computed(() => {
  if (authStore.error?.code !== 'VALIDATION_ERROR') {
    return currentErrorMessage.value
  }
  if (Object.keys(signupFieldErrors.value).length > 0) {
    return ''
  }
  return authStore.error?.message || ''
})

// ─── Mode switching ──────────────────────────────────────────────

const switchMode = (mode) => {
  authStore.setMode(normalizeMode(mode))
}

const handleModeToggle = () => {
  const nextMode = authStore.mode === 'signin' ? 'signup' : 'signin'
  switchMode(nextMode)
}

const handleForgotPassword = () => {
  switchMode('forgot-password')
}

const handleBackToSignin = () => {
  switchMode('signin')
}

// ─── Login handlers ──────────────────────────────────────────────

const updateLoginMobile = (value) => {
  authStore.loginMobile = String(value ?? '')
  if (authStore.error) authStore.error = null
}

const updateLoginPassword = (value) => {
  authStore.loginPassword = String(value ?? '')
  if (authStore.error) authStore.error = null
}

const handleLogin = async () => {
  const session = await authStore.login()
  if (session) {
    emit('success')
  }
}

// ─── Signup handlers ─────────────────────────────────────────────

const updateSignupDraft = (draft) => {
  authStore.signupDraft = { ...authStore.signupDraft, ...draft }
  if (authStore.error?.code === 'VALIDATION_ERROR') {
    authStore.error = null
  }
}

const signupPhoneMask = computed(() => {
  const digits = String(authStore.signupDraft.mobile ?? '').replace(/\D/g, '')
  if (digits.length >= 4) {
    return `+91 ${digits.slice(0, 2)}****${digits.slice(-2)}`
  }
  return '+91 ****'
})

const handleSignupSendOtp = async () => {
  const result = await authStore.signupValidateAndSendOtp(authStore.signupDraft)
  // If result is truthy, the store moved step to 'signup-otp' automatically
}

const updateSignupOtp = (otp) => {
  authStore.signupOtp.otp = String(otp ?? '')
  if (authStore.error) authStore.error = null
}

const handleSignupVerifyOtp = async () => {
  const session = await authStore.signupVerifyOtpAndCreate()
  if (session) {
    emit('success')
  }
}

const handleSignupResendOtp = async () => {
  await authStore.signupResendOtp()
}

const handleSignupOtpBack = () => {
  authStore.step = 'details'
  authStore.status = 'idle'
  authStore.error = null
  authStore.signupOtp.otp = ''
}

// ─── Forgot password handlers ────────────────────────────────────

const updateForgotDraft = (draft) => {
  authStore.forgotDraft = { ...authStore.forgotDraft, ...draft }
  if (authStore.error) authStore.error = null
}

const handleForgotSendOtp = async () => {
  await authStore.sendForgotOtp(authStore.forgotDraft.mobile)
}

const handleForgotResendOtp = async () => {
  await authStore.resendForgotOtp()
}

const handleResetPassword = async () => {
  const result = await authStore.resetPassword()
  if (result) {
    // Password reset => user is now on signin screen
  }
}

// ─── Lifecycle ───────────────────────────────────────────────────

watch(
  () => props.initialMode,
  (mode) => {
    switchMode(mode)
  },
  { immediate: true }
)

onMounted(() => {
  authStore.hydrateSession()
  clockIntervalId = window.setInterval(() => {
    authStore.refreshClock()
  }, 1000)
})

onBeforeUnmount(() => {
  if (clockIntervalId) {
    window.clearInterval(clockIntervalId)
    clockIntervalId = null
  }
})
</script>

<style scoped>
.auth-card {
  border: 1px solid #ececec;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 8px 35px rgba(17, 24, 39, 0.08);
}

/* Disable the theme hover animation that turns buttons black */
.auth-card :deep(.primary-btn1)::after {
  display: none !important;
}
.auth-card :deep(.primary-btn1:hover) {
  opacity: 0.88;
}
.auth-card :deep(.primary-btn1:hover > span) {
  transform: none !important;
  opacity: 1 !important;
}

@media (max-width: 767px) {
  .auth-card {
    padding: 18px;
  }
}
</style>
